import { Grid, Stack, TextField, FormControlLabel, Checkbox, Autocomplete, InputAdornment } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { ReactNode, useEffect, useRef } from "react";
import { BlacklistNameIndividual } from "./types";

type ReviewFieldHoverProps = { onMouseEnter: () => void; onMouseLeave: () => void };

interface Props {
  individualInfo: BlacklistNameIndividual;
  setIndividualInfo: (e: any, value: any) => void;
  nameError: boolean;
  onBlurIndividual: () => void;
  isEditMode?: boolean;
  disabled?: boolean;
  renderReviewActions?: (fieldId: string, displayedValue: unknown) => ReactNode;
  getReviewFieldHoverProps?: (fieldId: string) => ReviewFieldHoverProps;
  getReviewFieldHighlightSx?: (fieldId: string) => SxProps<Theme>;
}
export default function IndividualContactInfo(props: Props) {
  const titles = ["Mr.", "Ms.", "Mrs.", "Dr.", "Prof."];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 81 }, (_, index) => (currentYear - index).toString());
  const isLookupCodeSet = Boolean(props.individualInfo.lookupCode);
  const locked = useRef(props.individualInfo);
  const ro = (val: any) => Boolean(props.disabled) || (props.isEditMode && Boolean(val));

  useEffect(() => {
    if (!props.disabled && !props.individualInfo?.isAttorney) {
      props.setIndividualInfo("yearAdmittedToBar", null);
    }
  }, [props.disabled, props.individualInfo?.isAttorney, props.setIndividualInfo]);
  return (
    <>
      <Stack direction="row" justifyContent="space-between" marginTop="40px" spacing={1}>
        <Grid item md={6} xs={12}>
          <Autocomplete
            options={titles}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Title"
                {...props.getReviewFieldHoverProps?.("personTitle")}
                sx={props.getReviewFieldHighlightSx?.("personTitle")}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {props.renderReviewActions && (
                        <InputAdornment position="end">{props.renderReviewActions("personTitle", props.individualInfo?.title)}</InputAdornment>
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
            onChange={(e, value) => {
              props.setIndividualInfo("title", value);
            }}
            value={props.individualInfo?.title}
            disabled={isLookupCodeSet || ro(locked.current?.title)}
          />
        </Grid>
        <Grid item md={8} xs={12}>
          <TextField
            label="First Name"
            value={props.individualInfo?.firstName}
            {...props.getReviewFieldHoverProps?.("personFirstName")}
            sx={props.getReviewFieldHighlightSx?.("personFirstName")}
            onChange={(e) => props.setIndividualInfo("firstName", e.target.value)}
            disabled={isLookupCodeSet || ro(locked.current?.firstName)}
            InputProps={{
              endAdornment: props.renderReviewActions ? (
                <InputAdornment position="end">{props.renderReviewActions("personFirstName", props.individualInfo?.firstName)}</InputAdornment>
              ) : undefined
            }}
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <TextField
            label="Middle"
            value={props.individualInfo?.middleName}
            {...props.getReviewFieldHoverProps?.("personMiddleName")}
            sx={props.getReviewFieldHighlightSx?.("personMiddleName")}
            onChange={(e) => props.setIndividualInfo("middleName", e.target.value)}
            disabled={isLookupCodeSet || ro(locked.current?.middleName)}
            InputProps={{
              endAdornment: props.renderReviewActions ? (
                <InputAdornment position="end">{props.renderReviewActions("personMiddleName", props.individualInfo?.middleName)}</InputAdornment>
              ) : undefined
            }}
          />
        </Grid>
        <Grid item md={8} xs={12}>
          <TextField
            label="Last Name"
            value={props.individualInfo?.lastName}
            {...props.getReviewFieldHoverProps?.("personLastName")}
            sx={props.getReviewFieldHighlightSx?.("personLastName")}
            onChange={(e) => props.setIndividualInfo("lastName", e.target.value)}
            error={props.nameError}
            onBlur={props.onBlurIndividual}
            disabled={isLookupCodeSet || ro(locked.current?.lastName)}
            InputProps={{
              endAdornment: props.renderReviewActions ? (
                <InputAdornment position="end">{props.renderReviewActions("personLastName", props.individualInfo?.lastName)}</InputAdornment>
              ) : undefined
            }}
          />
        </Grid>
        <Grid item md={3} xs={12}>
          <TextField
            label="Suffix"
            value={props.individualInfo?.suffix}
            {...props.getReviewFieldHoverProps?.("personSuffix")}
            sx={props.getReviewFieldHighlightSx?.("personSuffix")}
            onChange={(e) => props.setIndividualInfo("suffix", e.target.value)}
            disabled={isLookupCodeSet || ro(locked.current?.suffix)}
            InputProps={{
              endAdornment: props.renderReviewActions ? (
                <InputAdornment position="end">{props.renderReviewActions("personSuffix", props.individualInfo?.suffix)}</InputAdornment>
              ) : undefined
            }}
          />
        </Grid>
      </Stack>
      <Stack direction="row" marginTop="30px" alignItems={"center"}>
        <Grid item md={3} xs={12} sx={{ display: "flex" }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            {...props.getReviewFieldHoverProps?.("personIsNotary")}
            sx={props.getReviewFieldHighlightSx?.("personIsNotary")}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={props.individualInfo?.isNotary}
                  onChange={(e, value) => props.setIndividualInfo("isNotary", value)}
                  disabled={ro(locked.current?.isNotary)}
                />
              }
              label="Is notary"
            />
            {props.renderReviewActions?.("personIsNotary", props.individualInfo?.isNotary)}
          </Stack>
        </Grid>
        <Grid item md={6} xs={12}>
          <TextField
            label="Tax ID"
            value={props.individualInfo?.taxID}
            {...props.getReviewFieldHoverProps?.("taxId")}
            sx={props.getReviewFieldHighlightSx?.("taxId")}
            onChange={(e) => props.setIndividualInfo("taxID", e.target.value)}
            fullWidth
            disabled={isLookupCodeSet || ro(locked.current?.taxID)}
            InputProps={{
              endAdornment: props.renderReviewActions ? (
                <InputAdornment position="end">{props.renderReviewActions("taxId", props.individualInfo?.taxID)}</InputAdornment>
              ) : undefined
            }}
          />
        </Grid>
      </Stack>
      <Stack direction="row" marginTop="30px" alignItems={"center"}>
        <Grid item md={3} xs={12} sx={{ display: "flex" }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            {...props.getReviewFieldHoverProps?.("personIsAttorney")}
            sx={props.getReviewFieldHighlightSx?.("personIsAttorney")}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={props.individualInfo?.isAttorney}
                  onChange={(e, value) => props.setIndividualInfo("isAttorney", value)}
                  disabled={ro(locked.current?.isAttorney)}
                />
              }
              label="Is attorney"
            />
            {props.renderReviewActions?.("personIsAttorney", props.individualInfo?.isAttorney)}
          </Stack>
        </Grid>
        {props.individualInfo?.isAttorney && (
          <Grid item md={6} xs={12}>
            <Autocomplete
              options={years}
              onChange={(e, value) => props.setIndividualInfo("yearAdmittedToBar", value)}
              value={props.individualInfo?.yearAdmittedToBar}
              disabled={ro(locked.current?.yearAdmittedToBar)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Admitted to Bar"
                  placeholder="Admitted to Bar"
                  {...props.getReviewFieldHoverProps?.("personYearAdmittedToBar")}
                  sx={props.getReviewFieldHighlightSx?.("personYearAdmittedToBar")}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {props.renderReviewActions && (
                          <InputAdornment position="end">
                            {props.renderReviewActions("personYearAdmittedToBar", props.individualInfo?.yearAdmittedToBar)}
                          </InputAdornment>
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          </Grid>
        )}
      </Stack>
    </>
  );
}
